import React, { useEffect, useMemo, useState } from 'react';
import '../CSS/NewCrit.css';

const formatDate = (dateString) => {
  if (!dateString) return '';
  return dateString.split('T')[0];
};

const getRiskColor = (level) => {
  switch ((level || '').toLowerCase()) {
    case 'low':
      return '#4CAF50';
    case 'elevated':
      return '#FFC107';
    case 'high':
      return '#F44336';
    default:
      return '#9E9E9E';
  }
};

const NewCrit = () => {
  const [newcrit, setNewCrit] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [dateSearch, setDateSearch] = useState('');
  const [locationSearch, setLocationSearch] = useState('');
  const [actionTypeSearch, setActionTypeSearch] = useState('');
  const [levelSearch, setLevelSearch] = useState('');
  const [summarySearch, setSummarySearch] = useState('');

  useEffect(() => {
    fetch('/api/hello')
      .then((response) => {
        if (!response.ok) throw new Error('Failed to fetch NewCrit data');
        return response.json();
      })
      .then((data) => {
        setNewCrit(data.newcrit || []);
        setLoading(false);
      })
      .catch((error) => {
        console.error('Error fetching NewCrit data:', error);
        setError('Failed to load data.');
        setLoading(false);
      });
  }, []);

  const filteredData = useMemo(() => {
    const normalizedDateSearch = dateSearch.trim().toLowerCase();
    const normalizedLocationSearch = locationSearch.trim().toLowerCase();
    const normalizedActionTypeSearch = actionTypeSearch.trim().toLowerCase();
    const normalizedLevelSearch = levelSearch.trim().toLowerCase();
    const normalizedSummarySearch = summarySearch.trim().toLowerCase();

    const noFiltersApplied =
      !normalizedDateSearch &&
      !normalizedLocationSearch &&
      !normalizedActionTypeSearch &&
      !normalizedLevelSearch &&
      !normalizedSummarySearch;

    const safeString = (value) => String(value ?? '').toLowerCase();

    const sortedData = [...newcrit].sort((a, b) => {
      const dateA = new Date(a.fields?.Date || 0);
      const dateB = new Date(b.fields?.Date || 0);
      return dateB - dateA;
    });

    if (noFiltersApplied) return sortedData;

    return sortedData.filter((item) => {
      const fields = item.fields || {};

      const dateValue = safeString(formatDate(fields.Date));
      const locationValue = safeString(
        [fields.Country, fields.State, fields.City, fields.Location].join(' ')
      );
      const actionTypeValue = safeString(fields.ActionType);
      const levelValue = safeString(fields.Level);
      const summaryValue = safeString(fields.Summary);

      return (
        (!normalizedDateSearch || dateValue.includes(normalizedDateSearch)) &&
        (!normalizedLocationSearch || locationValue.includes(normalizedLocationSearch)) &&
        (!normalizedActionTypeSearch || actionTypeValue.includes(normalizedActionTypeSearch)) &&
        (!normalizedLevelSearch || levelValue.includes(normalizedLevelSearch)) &&
        (!normalizedSummarySearch || summaryValue.includes(normalizedSummarySearch))
      );
    });
  }, [
    newcrit,
    dateSearch,
    locationSearch,
    actionTypeSearch,
    levelSearch,
    summarySearch,
  ]);

  if (loading) {
    return (
      <div className="newcrit-page">
        <div className="newcrit-head">
          <h1 className="newcrit-heading">Critical Insights Dashboard</h1>
          <p className="newcrit-status">Loading...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="newcrit-page">
        <div className="newcrit-head">
          <h1 className="newcrit-heading">Critical Insights Dashboard</h1>
          <p className="newcrit-status error">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="newcrit-page">
      <div className="newcrit-head">
        <h1 className="newcrit-heading">Critical Insights Dashboard</h1>

        <div className="newcrit-description-box">
          <p className="newcrit-description">
            Near–real-time civil unrest and general critical insight data providing concise,
            daily intelligence updates summarizing verified protests, unrest, and security
            disruptions. This dashboard combines open-source monitoring with human verification
            and trend context to enhance company awareness of possible travel or operational
            disruption risks.
          </p>

          <p className="newcrit-description">
            If interested in obtaining elevated, designated travel or general area monitoring
            for specific timeframes and cities — whether leadership is traveling or conference
            attendance — please reach out to the Watchroom to set up this ad-hoc service.
          </p>

          <details className="newcrit-criteria">
            <summary>The following data was developed via the following criteria to standardize reporting</summary>

            <div className="newcrit-criteria-content">
              <p><strong>Ratings:</strong> 1–5</p>

              <p><strong>Weighted Score Components:</strong></p>
              <ul>
                <li>Size — 40% weighted score in total</li>
                <li>Violence — 40% weighted score in total</li>
                <li>Disruption — 20% weighted score in total</li>
              </ul>

              <p><strong>Colors:</strong></p>
              <ul>
                <li><strong>Green – Low:</strong> Routine monitoring; minimal impact.</li>
                <li><strong>Yellow – Elevated:</strong> Likely travel or operational impact; review contingencies.</li>
                <li><strong>Red – High:</strong> Significant safety concern; avoid area.</li>
              </ul>
            </div>
          </details>
        </div>

        <div className="newcrit-toolbar multi-search">
          <input
            type="text"
            className="newcrit-search"
            placeholder="Search date..."
            value={dateSearch}
            onChange={(e) => setDateSearch(e.target.value)}
          />

          <input
            type="text"
            className="newcrit-search"
            placeholder="Search location..."
            value={locationSearch}
            onChange={(e) => setLocationSearch(e.target.value)}
          />

<select
  className="newcrit-search"
  value={actionTypeSearch}
  onChange={(e) => setActionTypeSearch(e.target.value)}
>
  <option value="">All Categories</option>
  <option value="Protest Activity">Protest Activity</option>
  <option value="March">March</option>
  <option value="Violent Unrest">Violent Unrest</option>
  <option value="Property Destruction">Property Destruction</option>
  <option value="Arson">Arson</option>
  <option value="Blockade">Blockade</option>
  <option value="Occupation">Occupation</option>
  <option value="Strike/Boycott">Strike/Boycott</option>
  <option value="Executive Targeting">Executive Targeting</option>
  <option value="General Advisory">General Advisory</option>
  <option value="Planned Action">Planned Action</option>
</select>

          <input
            type="text"
            className="newcrit-search"
            placeholder="Search level..."
            value={levelSearch}
            onChange={(e) => setLevelSearch(e.target.value)}
          />

          <input
            type="text"
            className="newcrit-search"
            placeholder="Search summary..."
            value={summarySearch}
            onChange={(e) => setSummarySearch(e.target.value)}
          />

          <button
            type="button"
            className="newcrit-clear-btn"
            onClick={() => {
              setDateSearch('');
              setLocationSearch('');
              setActionTypeSearch('');
              setLevelSearch('');
              setSummarySearch('');
            }}
          >
            Clear Filters
          </button>
        </div>
      </div>

      <div className="newcrit-grid-wrapper">
        <table className="newcrit-grid">
          <thead>
            <tr>
              <th>Date</th>
              <th>Location</th>
              <th>Action Type</th>
              <th>Size</th>
              <th>Violence</th>
              <th>Disruption</th>
              <th>Score</th>
              <th>Level</th>
              <th>Risk Indicator</th>
              <th>Summary</th>
              <th>Hyper Links</th>
            </tr>
          </thead>

          <tbody>
            {filteredData.length > 0 ? (
              filteredData.map((item) => {
                const fields = item.fields || {};
                const level = fields.Level || '';
                const summary = fields.Summary || '';

                return (
                  <tr key={item.id}>
                    <td>{formatDate(fields.Date)}</td>
                    <td>{fields.Country || fields.Location || ''}</td>
                    <td>{fields.ActionType || ''}</td>
                    <td>{fields.Size ?? ''}</td>
                    <td>{fields.Violence ?? ''}</td>
                    <td>{fields.Disruption ?? ''}</td>
                    <td>{fields.Score ?? ''}</td>
                    <td>
                      <span className={`risk-level risk-${level.toLowerCase()}`}>
                        {level}
                      </span>
                    </td>
                    <td>
                      <div className="risk-indicator-cell">
                        <span
                          className="risk-dot"
                          style={{ backgroundColor: getRiskColor(level) }}
                        ></span>
                      </div>
                    </td>
                    <td className="summary-cell">{summary}</td>
                    <td>
                      {fields.HyperLinks ? (
                        <a
                          href={fields.HyperLinks}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="newcrit-link"
                        >
                          View
                        </a>
                      ) : ''}
                    </td>
                  </tr>
                );
              })
            ) : (
              <tr>
                <td colSpan="11" className="no-results">
                  No records found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default NewCrit;